import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { TestimonialResponseModel } from "../../data/models/testimonial-model";
import showToast from "@/core/utils/show-toast";
import errorMessages from "@/core/utils/error-messages";
import axios from "@/core/utils/axios-instance";

/**
 * Generic mutation wrapper with toast notifications and navigation
 */
const useTestimonialMutation = (
  mutationFn: (data: any) => Promise<TestimonialResponseModel>,
  options?: {
    onSuccessNavigate?: string;
    invalidateQueries?: string[];
  }
) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation<TestimonialResponseModel, Error, any>({
    mutationFn,
    onSuccess: (data: TestimonialResponseModel) => {
      showToast(data.message, "success");

      // Invalidate queries if specified
      if (options?.invalidateQueries) {
        options.invalidateQueries.forEach((queryKey) => {
          queryClient.invalidateQueries({ queryKey: [queryKey] });
        });
      }

      // Navigate if specified
      if (options?.onSuccessNavigate) {
        setTimeout(() => {
          navigate(options.onSuccessNavigate as string);
        }, 100); // Small delay to ensure toast shows
      }
    },
    onError: (err: any) => {
      console.error("Mutation error:", err);
      showToast(errorMessages(err), "error");
    },
  });
};


/**
 * Generic query wrapper
 */
const useTestimonialQuery = (
  queryFn: () => Promise<TestimonialResponseModel>,
  queryKey?: readonly unknown[],
  enabled = false
) => {
  return useQuery<TestimonialResponseModel, Error, any>({
    queryKey: queryKey ?? ["default-testimonial-query-key"],
    queryFn,
    enabled,
  });
};

/**
 * Helpers
 */
const getAuthConfig = () => {
  const token = localStorage.getItem("token");
  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
};

/**
 * Create Testimonial (multipart/form-data)
 */
const createTestimonial = async (
  formData: FormData
): Promise<TestimonialResponseModel> => {
  const response = await axios.post<TestimonialResponseModel>(
    "/testimonial/create",
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
        ...getAuthConfig().headers,
      },
    }
  );
  return response.data;
};

/**
 * Get All Testimonials
 */
const getTestimonials = async (): Promise<TestimonialResponseModel> => {
  const response =
    await axios.get<TestimonialResponseModel>("/testimonial/all");
  return response.data;
};

/**
 * Get Testimonial By ID
 */
const getTestimonialById = async (
  id: string
): Promise<TestimonialResponseModel> => {
  const response = await axios.get<TestimonialResponseModel>(
    `/testimonial/single/${id}`
  );
  return response.data;
};

/**
 * Update testimonial (multipart/form-data if file present)
 */
const updateTestimonial = async (id: string, formData: FormData) => {
  const response = await axios.put<TestimonialResponseModel>(
    `/testimonial/update/${id}`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
        ...getAuthConfig().headers,
      },
    }
  );
  return response.data;
};

/**
 * Delete Testimonial
 */
const deleteTestimonial = async (id: string): Promise<TestimonialResponseModel> => {
  const response = await axios.delete<TestimonialResponseModel>(
    `/testimonial/delete/${id}`,
    getAuthConfig()
  );
  return response.data;
};

/**
 * React Query Hooks with Navigation
 */
export const useCreateTestimonial = () => {
  return useTestimonialMutation(createTestimonial, {
    invalidateQueries: ["get-testimonials"],
  });
};

export const useGetTestimonials = () => {
  return useTestimonialQuery(getTestimonials, ["get-testimonials"], true);
};

export const useGetTestimonialById = (id: string, enabled = false) => {
  return useTestimonialQuery(
    () => getTestimonialById(id),
    ["get-testimonial-by-id", id],
    enabled
  );
};

export const useUpdateTestimonial = (id: string) => {
  return useTestimonialMutation((data) => updateTestimonial(id, data), {
    onSuccessNavigate: "/dashboard/testimonials",
    invalidateQueries: ["get-testimonials", "get-testimonial-by-id"],
  });
};

export const useDeleteTestimonial = () => {
 return useTestimonialMutation((id: string) => deleteTestimonial(id))};
